import { EventType } from "@/analytics/analytics-events";
import { DownloadGeneratedFloorPlanProperties } from "@/analytics/floor-plan-generation-events-properties";
import {
  FeedbackKey,
  useFeedbackContext,
} from "@/components/common/feedback/feedback-context";
import { BetaBadge } from "@/components/ui/beta-badge";
import { LazyFaroMenu } from "@/components/ui/lazy-faro-menu";
import { useLoadIElementsOnDemand } from "@/hooks/use-load-ielement-on-demand";
import { runtimeConfig } from "@/runtime-config";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import {
  isLightweightDataSession,
  isLightweightGenericDataset,
} from "@/store/lightweight-elements-discriminators";
import {
  selectLightweightAncestor,
  selectLightweightElement,
  selectLightweightElementsSlice,
} from "@/store/lightweight-elements-selectors";
import type {
  LightweightElement,
  LightweightElementsState,
} from "@/store/lightweight-elements-slice";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { useAppSelector } from "@/store/store-hooks";
import { Captur3dFloorplanGenerationTask } from "@/utils/background-tasks";
import { downloadFile } from "@/utils/download";
import { createMailToLink } from "@/utils/redirects";
import {
  CopyIcon,
  FaroButton,
  FaroMenuItem,
  FaroMenuSeparator,
  FaroText,
  grey,
  NO_TRANSLATE_CLASS,
  NoTranslate,
  RemainingTimeLabel,
  TranslateVar,
  useToast,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  GUID,
  IElement,
  IElementAttachment,
  isIElementAttachment,
  isIElementGenericPointCloud,
  isValid,
} from "@faro-lotv/ielement-types";
import {
  createSignedUrlForIElementUri,
  selectChildDepthFirst,
  selectIElement,
  selectProjectId,
} from "@faro-lotv/project-source";
import {
  BackgroundTaskState,
  isBackgroundTaskActive,
  ProjectApi,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { Stack } from "@mui/system";
import { createSelector } from "@reduxjs/toolkit";
import { useCallback, useState, type JSX } from "react";
import { CardError } from "../common/card-error";
import { CardProgress } from "../common/card-progress";
import { CardAction, GenericCardLayout } from "../layouts/generic-card-layout";
import { getTaskStatusLabel } from "../task-descriptions";

type FloorplanGenerationCardProps = {
  /** The export task */
  task: Captur3dFloorplanGenerationTask;
};

/** @returns The card for the floor plan generation task */
export function FloorplanGenerationCard({
  task,
}: FloorplanGenerationCardProps): JSX.Element {
  const refElement = useAppSelector(selectRefElement(task.iElementId));

  const { projectApiClient } = useApiClientContext();

  const downloadActions = useAppSelector(
    selectDownloadActions(projectApiClient, task.iElementId),
  );

  const orderId = useAppSelector(
    selectFloorPlanGenerationOrderId(task.iElementId),
  );

  // The menu requires the element to be loaded.
  const { isLoaded, isLoading, startLoadIfNeeded } = useLoadIElementsOnDemand(
    task.iElementId,
  );

  let cardName: string | JSX.Element = "Export";
  if (refElement?.name) {
    cardName = <NoTranslate>{refElement.name}</NoTranslate>;
  }

  return (
    <GenericCardLayout
      name={cardName}
      subText={
        <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
          Floor plan generation
        </Stack>
      }
      startTime={task.createdAt}
      // Pass a dummy menu when not loaded, so the button to open a menu is still shown and can trigger the loading.
      menu={
        isLoaded
          ? downloadActions
          : [{ name: "Loading...", callback: () => {} }]
      }
      menuButtonLabel="Download"
      menuIsLoading={isLoading}
      onMenuOpened={startLoadIfNeeded}
    >
      {task.state === BackgroundTaskState.failed && <CardError task={task} />}
      {isBackgroundTaskActive(task.state) ? (
        <CardProgress
          label={getTaskStatusLabel(task)}
          subLabel={
            task.expectedEnd ? (
              <NoTranslate>
                (<RemainingTimeLabel expectedEnd={task.expectedEnd} />)
              </NoTranslate>
            ) : undefined
          }
          task={task}
          action={
            <FloorPlanGenerationSupportAndFeedback
              orderId={orderId}
              allowFeedback={false}
              isLoading={isLoading}
              onMenuOpened={startLoadIfNeeded}
            />
          }
        />
      ) : (
        <FloorPlanGenerationSupportAndFeedback
          orderId={orderId}
          allowFeedback={task.state === BackgroundTaskState.succeeded}
          isLoading={isLoading}
          onMenuOpened={startLoadIfNeeded}
        />
      )}
    </GenericCardLayout>
  );
}

/**
 * A memoized selector to determine the reference element for a given element ID
 *
 * @param state The state of the app store
 * @param id The data of the element to find the reference for
 * @returns the memoized selector
 */
const selectRefElement = curryAppSelector(
  createSelector(
    [selectLightweightElementsSlice, (state: RootState, id: GUID | null) => id],
    computeRefElement,
  ),
);

/**
 * @param lightweightElements The lightweight elements slice of the store
 * @param id The ID of the element to find the reference for
 * @returns the reference element for a given element ID
 */
function computeRefElement(
  lightweightElements: LightweightElementsState,
  id: GUID | null,
): LightweightElement | undefined {
  if (!id) return;
  return selectLightweightAncestor(
    selectLightweightElement(id)({ lightweightElements }),
    (e) => isLightweightDataSession(e) || isLightweightGenericDataset(e),
  )({ lightweightElements });
}

type FloorPlanGenerationSupportAndFeedbackProps = {
  /** The orderId of the external service provider */
  orderId?: number;

  /** Whether feedback is allowed for this order */
  allowFeedback: boolean;

  /** Whether the iElement data required for the menu contents is still loading */
  isLoading: boolean;

  /** Callback to trigger loading the iElement data on demand */
  onMenuOpened(): void;
};

function FloorPlanGenerationSupportAndFeedback({
  orderId,
  allowFeedback,
  isLoading,
  onMenuOpened,
}: FloorPlanGenerationSupportAndFeedbackProps): JSX.Element {
  const { openToast } = useToast();

  const projectId = useAppSelector(selectProjectId);
  const orderDetails = `Sphere XG project ID: ${projectId}\nFloor plan order ID: ${orderId}`;

  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  const handleOpenMenu = useCallback(() => {
    onMenuOpened();
    setIsMenuOpen(true);
  }, [onMenuOpened]);

  const onContactSupport = useCallback(() => {
    window.open(
      createMailToLink(
        runtimeConfig.externalLinks.supportMail,
        "Floor plan generation support",
        `Hello, I'd like to request help with my floor plan generation order.\n\n${orderDetails}`,
      ),
      "_blank",
    );

    closeMenu();
  }, [closeMenu, orderDetails]);

  const { requestFeedback } = useFeedbackContext();
  const onRequestFeedback = useCallback(() => {
    requestFeedback({
      key: FeedbackKey.floorPlanGeneration,
      data: {
        question: "How would you rate the floor plan quality?",
        numberedLabels: ["Poor", undefined, undefined, undefined, "Excellent"],
        orderId,
      },
    });

    closeMenu();
  }, [closeMenu, orderId, requestFeedback]);

  const onCopyOrderDetails = useCallback(() => {
    navigator.clipboard.writeText(orderDetails);
    openToast({ title: "Copied to clipboard", variant: "success" });

    closeMenu();
  }, [orderDetails, openToast, closeMenu]);

  return (
    <>
      <FaroButton
        ref={setButtonRef}
        variant="ghost"
        size="xs"
        onClick={handleOpenMenu}
        sx={{ alignSelf: "flex-start" }}
      >
        Support & Feedback
      </FaroButton>
      <LazyFaroMenu
        isLoading={isLoading}
        open={isMenuOpen}
        anchorEl={buttonRef}
        onClose={closeMenu}
      >
        <FaroMenuItem
          label="Contact support"
          secondaryText="Make sure to include your order ID"
          onClick={onContactSupport}
        />
        <FaroMenuItem
          label="Give feedback"
          disabled={!allowFeedback}
          secondaryText="How is the floor plan quality?"
          onClick={onRequestFeedback}
        />
        <FaroMenuSeparator />
        <FaroMenuItem
          label={
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                color: grey[600],
              }}
            >
              <FaroText variant="bodyS" sx={{ color: "inherit" }}>
                Order ID:{" "}
                <TranslateVar name="orderId">{orderId ?? "n/a"}</TranslateVar>
              </FaroText>
              <CopyIcon sx={{ color: "inherit", fontSize: 16 }} />
            </Stack>
          }
          onClick={onCopyOrderDetails}
        />
      </LazyFaroMenu>
    </>
  );
}

/**
 * The file types supported for download.
 * The file types are ordered by importance for the customer.
 * Some formats are marked as "beta", as they do not meet our quality standards yet.
 */
const SUPPORTED_FLOOR_PLAN_FORMATS = [
  { fileSuffix: ".pdf", isBeta: false },
  { fileSuffix: ".dwg", isBeta: true },
  { fileSuffix: ".rvt", isBeta: true },
  { fileSuffix: ".png", isBeta: false },
  { fileSuffix: ".jpg", isBeta: false },
  { fileSuffix: ".svg", isBeta: false },
];

/**
 * @returns The actions for downloading the generated floor plan in different file formats
 * @param state Current app state
 * @param projectApi Project api instance to sign urls with
 * @param elementId The id of the floor plan generation layer containing all attachments
 */
const selectDownloadActions = curryAppSelector(
  createSelector(
    [
      (state: RootState) => state,
      (state: RootState, projectApi: ProjectApi) => projectApi,
      (state: RootState, projectApi: ProjectApi, elementId?: GUID | null) =>
        selectIElement(elementId)(state),
      (state: RootState) =>
        selectHasFeature(Features.FloorPlanBetaFormats)(state),
    ],
    (state, projectApi, floorPlanGenerationLayer, canDownloadBetaFormats) => {
      if (!floorPlanGenerationLayer) return [];

      return SUPPORTED_FLOOR_PLAN_FORMATS.filter(
        ({ isBeta }) => !isBeta || canDownloadBetaFormats,
      )
        .map(({ fileSuffix, isBeta }) =>
          selectDownloadActionForType(
            projectApi,
            floorPlanGenerationLayer,
            fileSuffix,
            isBeta,
          )(state),
        )
        .filter(isValid);
    },
  ),
);

/**
 * @returns The menu action to download a floor plan in a specific file type, or undefined if the file type is not available.
 * @param projectApi Project api instance to sign urls with
 * @param floorPlanGenerationLayer The iElement containing the generated floor plan attachments
 * @param fileSuffix The file type to download.
 * @param isBetaFormat Whether the file type is considered beta-quality
 */
function selectDownloadActionForType(
  projectApi: ProjectApi,
  floorPlanGenerationLayer: IElement,
  fileSuffix: string,
  isBetaFormat: boolean,
) {
  return (state: RootState): CardAction | undefined => {
    const attachment = selectChildDepthFirst(
      floorPlanGenerationLayer,
      (el): el is IElementAttachment =>
        isIElementAttachment(el) &&
        !!el.fileName?.toLowerCase().endsWith(fileSuffix),
    )(state);

    if (!attachment) return;

    return {
      name: (
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", gap: 1, minWidth: "80px" }}
        >
          <FaroText variant="bodyM" className={NO_TRANSLATE_CLASS}>
            {fileSuffix}
          </FaroText>
          {isBetaFormat && <BetaBadge />}
        </Stack>
      ),
      async callback() {
        Analytics.track<DownloadGeneratedFloorPlanProperties>(
          EventType.downloadGeneratedFloorPlan,
          { fileFormat: fileSuffix },
        );

        const signedDownloadUrl = await createSignedUrlForIElementUri(
          projectApi,
          attachment,
        ).requestSignedUrl();

        downloadFile(signedDownloadUrl, undefined, true);
      },
    };
  };
}

/**
 * @returns The Captur3d order ID for the floor plan generation task, or undefined if not available
 * @param state Current app state
 * @param elementId The id of the floor plan generation layer containing all attachments
 */
const selectFloorPlanGenerationOrderId = curryAppSelector(
  createSelector(
    [
      (state: RootState) => state,
      (state: RootState, elementId?: GUID | null) =>
        selectIElement(elementId)(state),
    ],
    (state, floorPlanGenerationLayer) => {
      if (!floorPlanGenerationLayer) return;

      const orderIdMetaData = selectChildDepthFirst(
        floorPlanGenerationLayer,
        isIElementGenericPointCloud,
      )(state)?.metaDataMap?.floorplanCaptur3dOrderId;

      if (typeof orderIdMetaData !== "number") return;

      return orderIdMetaData;
    },
  ),
);
